import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { motion, AnimatePresence } from 'framer-motion';
import { SqlCodeBlock } from './SqlCodeBlock';
import {
  selectIsRoleHelpExpanded,
  selectIsUsernameHelpExpanded,
  selectIsWarehouseHelpExpanded,
  setRoleHelpExpanded,
  setUsernameHelpExpanded,
  setWarehouseHelpExpanded,
} from '../reducers/onboardingSlice';
import { RootState } from 'app/store';

/**
 * Type for help panel identifier
 */
type HelpPanelType = 'role' | 'username' | 'warehouse';

interface SetupHelpPanelProps {
  /**
   * Title displayed on the toggle button
   */
  title: string;
  /**
   * Purpose description (1-2 sentences)
   */
  purpose: string;
  /**
   * SQL code blocks to display
   */
  sqlBlocks: Array<{
    code: string;
    runAsRole?: string;
    label?: string;
  }>;
  /**
   * "What to do next" checklist items
   */
  nextSteps: string[];
  /**
   * Additional notes or requirements
   */
  notes?: string[];
  /**
   * Unique ID for accessibility
   */
  id: string;
  /**
   * Help panel type for Redux state management (replaces storageKey)
   * If not provided, panel state will not be persisted
   */
  helpType?: HelpPanelType;
  /**
   * @deprecated Use helpType instead. This prop is kept for backward compatibility
   * and will be removed in a future version
   */
  storageKey?: string;
}

/**
 * SetupHelpPanel Component
 *
 * A collapsible panel that provides setup instructions, SQL snippets,
 * and guidance for Snowflake configuration tasks.
 *
 * Features:
 * - Collapsible UI with smooth animations
 * - Copy-ready SQL code blocks
 * - Accessibility compliant (ARIA attributes, keyboard navigation)
 * - Persists expanded/collapsed state in Redux (via redux-persist)
 *
 * @component
 * @example
 * ```tsx
 * <SetupHelpPanel
 *   id="warehouse-help"
 *   title="Setup help"
 *   purpose="Create a dedicated warehouse for DataOps tasks."
 *   sqlBlocks={[{ code: "CREATE WAREHOUSE...", runAsRole: "ACCOUNTADMIN" }]}
 *   nextSteps={["Paste SQL in worksheet", "Enter warehouse name in form"]}
 *   notes={["You can adjust size as needed"]}
 *   helpType="warehouse"
 * />
 * ```
 */
export const SetupHelpPanel: React.FC<SetupHelpPanelProps> = ({
  title,
  purpose,
  sqlBlocks,
  nextSteps,
  notes,
  id,
  helpType,
  storageKey,
}) => {
  const dispatch = useDispatch();

  // Map storageKey to helpType for backward compatibility
  // This ensures existing code continues to work
  const effectiveHelpType: HelpPanelType | undefined = (() => {
    if (helpType != null) {
      return helpType;
    }
    // Map legacy storageKey values to helpType
    if (storageKey === 'role-help-expanded') return 'role';
    if (storageKey === 'username-help-expanded') return 'username';
    if (storageKey === 'warehouse-help-expanded') return 'warehouse';
    return undefined;
  })();

  // Get the appropriate selector based on helpType
  const isExpanded = useSelector((state: RootState) => {
    if (effectiveHelpType === 'role') return selectIsRoleHelpExpanded(state);
    if (effectiveHelpType === 'username') return selectIsUsernameHelpExpanded(state);
    if (effectiveHelpType === 'warehouse') return selectIsWarehouseHelpExpanded(state);
    return false; // Default to collapsed if no helpType
  });

  const toggleExpanded = (): void => {
    const newExpanded = !isExpanded;

    // Dispatch the appropriate action based on helpType
    if (effectiveHelpType === 'role') {
      dispatch(setRoleHelpExpanded(newExpanded));
    } else if (effectiveHelpType === 'username') {
      dispatch(setUsernameHelpExpanded(newExpanded));
    } else if (effectiveHelpType === 'warehouse') {
      dispatch(setWarehouseHelpExpanded(newExpanded));
    }
    // If no helpType, panel toggles locally but doesn't persist
  };

  const buttonId = `${id}-button`;
  const regionId = `${id}-region`;

  return (
    <div className="mt-2">
      <button
        id={buttonId}
        onClick={toggleExpanded}
        className="inline-flex items-center gap-1 text-sm text-dataops-primary-blue hover:text-hover-primary-blue transition-colors duration-150"
        aria-expanded={isExpanded}
        aria-controls={regionId}
        type="button"
      >
        <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
        </motion.div>
        {title}
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            id={regionId}
            role="region"
            aria-labelledby={buttonId}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="mt-3 p-4 bg-dark-card-background border border-gray-200 rounded-md space-y-4">
              {/* Purpose */}
              <div>
                <h4 className="text-sm font-medium text-dataops-dark">Purpose:</h4>
                <p className="mt-1 text-sm text-primary-text">{purpose}</p>
              </div>

              {/* SQL Blocks */}
              <div className="space-y-3">
                {sqlBlocks.map((block, index) => (
                  <SqlCodeBlock
                    key={index}
                    code={block.code}
                    runAsRole={block.runAsRole}
                    copyLabel={block.label ?? 'Copy SQL'}
                  />
                ))}
              </div>

              {/* Next Steps */}
              <div>
                <h4 className="text-sm font-medium text-dataops-dark">What to do next:</h4>
                <ul className="mt-2 space-y-1 text-sm text-primary-text list-disc list-inside">
                  {nextSteps.map((step, index) => (
                    <li key={index}>{step}</li>
                  ))}
                </ul>
              </div>

              {/* Notes */}
              {notes !== undefined && notes.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-dataops-dark">Notes:</h4>
                  <ul className="mt-2 space-y-1 text-sm text-secondary-text list-disc list-inside">
                    {notes.map((note, index) => (
                      <li key={index}>{note}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
